// Hybrid logical clocks, exactly as biblestandard-api docs/integration/sync.md §3 specifies them:
// "<wall ms, 13 digits>-<counter, 4 hex>-<node, 16 hex>", so plain string comparison is clock order.
// The state is a plain value: the store persists it in the same transaction as the outbox write it
// stamped, so two tabs (one node id) can never issue the same clock.

export type HlcState = { lastMs: number; counter: number; node: string };

/** The server refuses a clock more than a day ahead of its own. */
export const MAX_AHEAD_MS = 24 * 60 * 60 * 1000;

const RE = /^(\d{13})-([0-9a-f]{4})-([0-9a-f]{16})$/;

export function formatHlc(ms: number, counter: number, node: string): string {
  return `${String(ms).padStart(13, "0")}-${counter.toString(16).padStart(4, "0")}-${node}`;
}

export function parseHlc(s: string): { ms: number; counter: number; node: string } | null {
  const m = RE.exec(s);
  return m ? { ms: Number(m[1]), counter: parseInt(m[2]!, 16), node: m[3]! } : null;
}

/** Stamp a local change: never goes backwards; an overflowing counter borrows a millisecond. */
export function tick(state: HlcState, now: number = Date.now()): { state: HlcState; hlc: string } {
  let { lastMs, counter } = state;
  const wall = Math.max(now, lastMs);
  if (wall === lastMs) counter += 1;
  else {
    lastMs = wall;
    counter = 0;
  }
  if (counter > 0xffff) {
    lastMs += 1;
    counter = 0;
  }
  const next = { lastMs, counter, node: state.node };
  return { state: next, hlc: formatHlc(lastMs, counter, state.node) };
}

/** Keep this clock ahead of every clock seen from the server, ignoring a broken peer's future clock. */
export function observe(state: HlcState, remote: string, now: number = Date.now()): HlcState {
  const r = parseHlc(remote);
  if (!r || r.ms > now + MAX_AHEAD_MS) return state;
  let { lastMs, counter } = state;
  const wall = Math.max(now, lastMs, r.ms);
  if (wall === lastMs && wall === r.ms) counter = Math.max(counter, r.counter) + 1;
  else if (wall === lastMs) counter += 1;
  else if (wall === r.ms) counter = r.counter + 1;
  else counter = 0;
  lastMs = wall;
  if (counter > 0xffff) {
    lastMs += 1;
    counter = 0;
  }
  return { lastMs, counter, node: state.node };
}
